import {
  IonApp,
  IonRouterOutlet,
  IonSplitPane,
  IonSpinner,
  setupIonicReact,
  useIonViewDidEnter,
 } from '@ionic/react';
 
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route, useParams } from 'react-router-dom';

// The old IonMenu-based <Menu /> is retired in favor of the AppNav rails.
// Kept in the tree commented-out for one cycle in case we need to revert.
// import Menu from './components/Menu';
import AppShell from './components/AppNav/AppShell';
import Page from './pages/Page';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Reset from './pages/Reset';

import Success from './components/containers/Success';


/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

/* App-wide navigation rails (OperatorRail / CrmRail / WorkspaceSwitcher /
 * ImpersonationBanner). Tokens go in at :root so the rails work outside
 * any feature-scoped CSS (.pm-app etc). Imported here once so every
 * surface sees the same chrome. */
import './components/AppNav/tokens.css';
import './components/AppNav/nav.css';
/* IonSplitPane / rail-pane sizing helpers — must load AFTER nav.css so
 * the modifier classes that read --rr-rail-*-w resolve correctly. */
import './App.css';

import { app, auth, user, getCurrentUser, readUser } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { lazy, Suspense, useEffect, useState } from 'react';
import { setUserState } from './redux/actions';
import { useDispatch } from 'react-redux';
import AdminReceivingNumbers from './components/containers/AdminReceivingNumbers';
import Integrations from './components/containers/Integrations';
import CTM from './components/containers/CTM';
import ProjectsList from './pages/ProjectsList';
import ProjectDetail from './pages/ProjectDetail';
import TemplateDetail from './pages/TemplateDetail';
import Settings from './pages/Settings';
import Feedback from './pages/Feedback';
import FeedbackInbox from './pages/FeedbackInbox';
import TeamsList from './pages/TeamsList';
import TeamDetail from './pages/TeamDetail';
import OperatorHub from './pages/OperatorHub';
import InternalTeam from './pages/InternalTeam';
import Recordings from './pages/Recordings';
import RecordingsAdmin from './pages/RecordingsAdmin';
import Logs from './pages/Logs';
import LogsAdmin from './pages/LogsAdmin';
import Permissions from './pages/Permissions';
import ExecDashboardAdmin from './pages/ExecDashboardAdmin';
import { RECORDINGS_AGENT_PRIMARY, RECORDINGS_AGENT_FOOTER } from './components/AppNav/config';
import NotificationCenter from './components/pm/NotificationCenter';
import { useIdleLogout } from './hooks/useIdleLogout';
import IdleWarning from './components/IdleWarning';

// ExecDashboard pulls in recharts; lazy-load it so those d3 deps only load on
// the /Operator/Exec routes and don't inflate the main bundle. The overview hub
// and silo stubs are lazy too (they share the exec-dashboard module).
const ExecDashboard = lazy(() => import('./pages/ExecDashboard'));
const ExecOverview = lazy(() => import('./pages/ExecOverview'));
const ExecFinancials = lazy(() => import('./pages/ExecFinancials'));
const ExecClients = lazy(() => import('./pages/ExecClients'));
const ExecPeople = lazy(() => import('./pages/ExecPeople'));
const ExecSales = lazy(() => import('./pages/ExecSales'));
const ExecMarketing = lazy(() => import('./pages/ExecMarketing'));
// Billing audit is its own silo (finance-gated, not part of /Operator/Exec) but
// is lazy for the same reason: only finance staff ever open it.
const BillingAudit = lazy(() => import('./pages/BillingAudit'));
// Website CMS — the marketing-site redirect table. Lazy because only the few
// people with a `website` grant ever open it.
const WebsiteRedirects = lazy(() => import('./pages/WebsiteRedirects'));
const WebsiteCaseStudies = lazy(() => import('./pages/WebsiteCaseStudies'));
const WebsiteAnalytics = lazy(() => import('./pages/WebsiteAnalytics'));

setupIonicReact();

// Parameterized redirect for legacy /Teams/:teamId → /Operator/Teams/:teamId.
// Defined as a child component (rather than a render prop) because
// IonRouterOutlet's page-stack manager works best with children-based routes.
const TeamsLegacyRedirect: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  return <Redirect to={`/Operator/Teams/${teamId}`} />;
};


//TODO  - Eventually want to expire login cookie after 10-15 min for security reasons. Mostly just if we decide to include PHI
const RoutingSystemAdmin: React.FC<{ email: string }> = ({ email }) => {
  return (
    <IonApp>
      <IonReactRouter>
        <NotificationCenter />
        <AppShell isAdmin={true} email={email}>
            <Route path="/Integrations" exact={true}>
              <Integrations />
            </Route>
            <Route path="/Integrations/CTM" exact={true}>
              <CTM />
            </Route>
            <Route path="/Operator" exact={true}>
              <OperatorHub />
            </Route>
            <Route path="/Operator/Receiving-Numbers" exact={true}>
              <AdminReceivingNumbers />
            </Route>
            <Route path="/Operator/Teams" exact={true}>
              <TeamsList />
            </Route>
            <Route path="/Operator/Teams/:teamId" exact={true}>
              <TeamDetail />
            </Route>
            <Route path="/Operator/Internal" exact={true}>
              <InternalTeam />
            </Route>
            <Route path="/Operator/Feedback" exact={true}>
              <FeedbackInbox />
            </Route>
            {/* Intake-logs portal — operator shell only. The pages self-gate
                on logsRole (viewer/admin), so being an operator alone isn't
                enough; access is granted per user from /Operator/Logs/Admin. */}
            <Route path="/Operator/Logs" exact={true}>
              <Logs />
            </Route>
            <Route path="/Operator/Logs/Admin" exact={true}>
              <LogsAdmin />
            </Route>
            {/* Unified permissions matrix — self-gates to operator super_admin. */}
            <Route path="/Operator/Permissions" exact={true}>
              <Permissions />
            </Route>
            {/* Executive dashboard — multi-view. /Exec is the overview hub;
                the silos are Service, Financials, Clients, and People. All
                self-gate on exec_dashboard (Financials also needs finance).
                Lazy-loaded (recharts). */}
            <Route path="/Operator/Exec" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecOverview />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Service" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecDashboard />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Financials" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecFinancials />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Clients" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecClients />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/People" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecPeople />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Sales" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecSales />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Marketing" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <ExecMarketing />
              </Suspense>
            </Route>
            <Route path="/Operator/Exec/Admin" exact={true}>
              <ExecDashboardAdmin />
            </Route>
            {/* Monthly billing audit — calls vs. submitted intake forms.
                Self-gates on exec_dashboard + finance (same pairing as the
                Financials silo); operators without finance see a notice. */}
            <Route path="/Operator/Billing-Audit" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <BillingAudit />
              </Suspense>
            </Route>
            {/* Website CMS. Every site-content surface hangs off this prefix and
                self-gates on the `website` permission. Case studies must be
                declared BEFORE the bare /Operator/Website match or the redirect
                table would shadow it, and analytics for the same reason. */}
            <Route path="/Operator/Website/Analytics" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <WebsiteAnalytics />
              </Suspense>
            </Route>
            <Route path="/Operator/Website/Case-Studies" exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <WebsiteCaseStudies />
              </Suspense>
            </Route>
            <Route path={['/Operator/Website', '/Operator/Website/Redirects']} exact={true}>
              <Suspense fallback={<IonSpinner />}>
                <WebsiteRedirects />
              </Suspense>
            </Route>
            {/* Template detail must come before the catch-all :projectId so
                Firebase IDs don't shadow it. */}
            <Route path="/Projects/Templates/:templateId" exact={true}>
              <TemplateDetail />
            </Route>
            {/* All four tab URLs share one Route → one IonRouterOutlet
                page-stack entry. Bouncing between sub-rail items no longer
                stacks fresh pages, which fixes the "el.classList undefined"
                lifecycle crash. react-router v5's array-of-paths handles
                both "/Projects" and the literal sub-paths in a single Route. */}
            <Route
              path={['/Projects', '/Projects/Roadmap', '/Projects/MyWork', '/Projects/Templates']}
              exact={true}
            >
              <ProjectsList />
            </Route>
            <Route path="/Projects/:projectId" exact={true}>
              <ProjectDetail />
            </Route>
            <Route path="/Settings" exact={true}>
              <Settings />
            </Route>
            <Route path="/Feedback" exact={true}>
              <Feedback />
            </Route>
            {/* Call-recordings portal. The page self-gates on recordingsRole
                (renders an access notice if the user has none), so it's safe
                to mount in both shells. */}
            <Route path="/Recordings" exact={true}>
              <Recordings />
            </Route>
            <Route path="/Recordings/Admin" exact={true}>
              <RecordingsAdmin />
            </Route>
            <Route path="/api/callback" exact={true}>
              <Success />
            </Route>
            <Route path="/api/bigben" exact={true}>
            </Route>
            {/* Back-compat redirects — must come last so IonRouterOutlet's
                page stack doesn't trip over them during normal navigation.
                Operators land on /Projects right after sign-in; that's the
                primary surface for internal staff. (The non-admin shell
                below still redirects to /Integrations because PM is
                operator-only.) */}
            <Route exact path="/"><Redirect to="/Projects" /></Route>
            <Route exact path="/Admin"><Redirect to="/Operator" /></Route>
            <Route exact path="/Admin/Receiving-Numbers"><Redirect to="/Operator/Receiving-Numbers" /></Route>
            <Route exact path="/Teams"><Redirect to="/Operator/Teams" /></Route>
            <Route path="/Teams/:teamId" exact={true}>
              <TeamsLegacyRedirect />
            </Route>
        </AppShell>
      </IonReactRouter>
    </IonApp>
  );
};


const RoutingSystemLoggedIn: React.FC<{ email: string }> = ({ email }) => {
  return (
    <IonApp>
      <IonReactRouter>
        <NotificationCenter />
        <AppShell isAdmin={false} email={email}>
            <Route path="/Integrations" exact={true}>
              <Integrations />
            </Route>
            <Route path="/Integrations/CTM" exact={true}>
              <CTM />
            </Route>
            {/* PROJECTS — operator-only. Non-admin users get redirected
                away from anything under /Projects. Server-side RTDB rules
                are the real gate (no project data is readable here), but
                this redirect avoids the empty-page UX for anyone who
                pastes a /Projects deep link. */}
            <Route path="/Projects">
              <Redirect to="/Integrations" />
            </Route>
            <Route path="/Operator/Teams" exact={true}>
              <TeamsList />
            </Route>
            <Route path="/Operator/Teams/:teamId" exact={true}>
              <TeamDetail />
            </Route>
            <Route path="/Settings" exact={true}>
              <Settings />
            </Route>
            <Route path="/Feedback" exact={true}>
              <Feedback />
            </Route>
            {/* Call-recordings portal — available to non-operator agents too;
                the page self-gates on recordingsRole. */}
            <Route path="/Recordings" exact={true}>
              <Recordings />
            </Route>
            <Route path="/Recordings/Admin" exact={true}>
              <RecordingsAdmin />
            </Route>
            <Route path="/api/callback" exact={true}>
              <Success />
            </Route>
            <Route path="/api/bigben" exact={true}>
            </Route>
            {/* Back-compat redirects — keep last. */}
            <Route exact path="/"><Redirect to="/Integrations" /></Route>
            <Route exact path="/Teams"><Redirect to="/Operator/Teams" /></Route>
            <Route path="/Teams/:teamId" exact={true}>
              <TeamsLegacyRedirect />
            </Route>
        </AppShell>
      </IonReactRouter>
    </IonApp>
  );
};

// Locked-down shell for provisioned call agents: users who have a
// recordingsRole but are NOT operators. They can reach ONLY the recordings
// section (+ Settings, which is just the sign-out surface). Every other path
// redirects to /Recordings, and the rail shows nothing else — so the operator
// panel / CRM / PM surfaces are fully out of reach for them.
const RoutingSystemRecordingsAgent: React.FC<{ email: string }> = ({ email }) => {
  return (
    <IonApp>
      <IonReactRouter>
        <AppShell
          isAdmin={false}
          email={email}
          crmItems={RECORDINGS_AGENT_PRIMARY}
          crmFooter={RECORDINGS_AGENT_FOOTER}
        >
          <Route path="/Recordings" exact={true}>
            <Recordings />
          </Route>
          <Route path="/Recordings/Admin" exact={true}>
            <RecordingsAdmin />
          </Route>
          {/* Settings is allowed only as the sign-out surface. */}
          <Route path="/Settings" exact={true}>
            <Settings />
          </Route>
          {/* Everything else — Integrations, /Operator/*, /Projects, /Teams,
              /Feedback, unknown paths — bounces to Recordings. */}
          <Route exact path="/">
            <Redirect to="/Recordings" />
          </Route>
          <Route>
            <Redirect to="/Recordings" />
          </Route>
        </AppShell>
      </IonReactRouter>
    </IonApp>
  );
};

const RoutingSystemLoggedOut: React.FC = () => {

  return (
    <IonApp>
      <IonReactRouter>
        <IonSplitPane contentId="main">
          <IonRouterOutlet id="main">
            <Route>
              <Redirect to="/Login"/>
            </Route>
            <Route path="/Login" exact={true}>
              <Login />
            </Route>
            <Route path="/Signup" exact={true}>
              <Signup />
            </Route>
            <Route path="/Reset" exact={true}>
              <Reset />
            </Route>
            <Route path="/api/bigben" exact={true}>
            </Route> 
          </IonRouterOutlet>
        </IonSplitPane>
      </IonReactRouter>
    </IonApp>
  );
};

const App: React.FC = () => {

  const [ loggedIn, setLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // True when the user has recordings access but is NOT an operator — they get
  // the locked recordings-only shell.
  const [recordingsOnly, setRecordingsOnly] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [ busy, setBusy] = useState(true);
  const dispatch = useDispatch();

  // HIPAA "automatic logoff" — sign the user out after 15 min of no activity.
  // Only armed while logged in; disabled on /Login and during the initial
  // auth check so we don't fight the sign-in flow.
  const idle = useIdleLogout(loggedIn);

  useEffect(()=>{
    getCurrentUser().then( (user: any) => {
      if (user){
        dispatch(setUserState(user));
        // Capture the email up-front so AppShell can render the operator
        // rail's footer name + initials without re-fetching.
        setEmail(user.email || '');

        readUser(user.uid).then((data: any) => {
          const restricted = (data && data.restricted) || {};
          const isOperator = restricted.role === 'admin';
          const recRole = restricted.recordingsRole;
          const hasRecordings =
            recRole === 'agent' || recRole === 'supervisor' || recRole === 'qa_lead' || recRole === 'admin';
          setIsAdmin(isOperator);
          // Recordings-only lockdown applies to non-operators with a recordings
          // role. Operators keep full access (and reach Recordings from their
          // own panel).
          setRecordingsOnly(!isOperator && hasRecordings);
          setLoggedIn(true);
          setBusy(false);
        });
      }
      else {
        setLoggedIn(false);
        setBusy(false);
      }
    }).catch(() => {
      setLoggedIn(false);
      setBusy(false);
    });
  });

  return (
    <IonApp>
      {busy ? (
        <IonSpinner />
      ) : loggedIn ? (
        isAdmin ? (
          <RoutingSystemAdmin email={email} />
        ) : recordingsOnly ? (
          <RoutingSystemRecordingsAgent email={email} />
        ) : (
          <RoutingSystemLoggedIn email={email} />
        )
      ) : (
        <RoutingSystemLoggedOut />
      )}
      {/* Final-minute countdown before HIPAA automatic logoff. */}
      {loggedIn && idle.warning && (
        <IdleWarning
          secondsLeft={idle.secondsLeft}
          onStay={idle.staySignedIn}
          onSignOut={idle.signOutNow}
        />
      )}
    </IonApp>
  );
};

export default App;
